import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'
import {
  EditOutlined,
  ExportOutlined,
  LoadingOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { Popover } from 'antd'
import { useAddAlert } from '@/hooks/useAddAlert'
import { taskTrackerApi } from '../../api/taskTracker.api'
import { BOARD_COLUMN_TITLE_MAX_LENGTH } from '../../lib/boardColumn.constants'
import { TASK_TRACKER_NO_DND_ATTR } from '../../lib/taskTrackerKanbanDnd.utils'
import { useCloseOnAncestorScroll } from '../../lib/hooks/useCloseOnAncestorScroll'
import { TaskTrackerBoardCard } from '../../store/taskTracker.types'
import TaskTrackerPermissionTooltip from '../TaskTrackerPermissionTooltip/TaskTrackerPermissionTooltip'
import classes from './index.module.css'

export type TaskTrackerEditableTitleProps = {
  taskId: number
  title: string
  variant: 'card' | 'drawer'
  disabled?: boolean
  readOnly?: boolean
  readOnlyReason?: string | null
  textClassName?: string
  stopPointerPropagation?: boolean
  canStartEditByTextClick?: boolean
  onOpenCard?: () => void
  onCopy?: () => void
  copyDisabled?: boolean
  copyDisabledReason?: string | null
  copyLoading?: boolean
  onTransfer?: () => void
  transferDisabled?: boolean
  transferDisabledReason?: string | null
  onDelete?: () => void
  deleteDisabled?: boolean
  deleteDisabledReason?: string | null
  onUpdated?: (card: TaskTrackerBoardCard) => void
  onEditingChange?: (isEditing: boolean) => void
}

const EditIcon = EditOutlined as unknown as FC<{ className?: string }>
const ExternalLinkIcon = ExportOutlined as unknown as FC<{ className?: string }>
const MoreIcon = MoreOutlined as unknown as FC<{ className?: string }>
const LoadingIcon = LoadingOutlined as unknown as FC<{
  spin?: boolean
  className?: string
}>
const TITLE_URL_REGEXP = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi
const URL_TRAILING_PUNCTUATION_REGEXP = /[.,!?;:)]$/

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

const stopEventPropagation = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

const normalizeTitleUrlHref = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`

const splitTitleUrl = (url: string) => {
  let cleanUrl = url
  let trailingText = ''

  while (URL_TRAILING_PUNCTUATION_REGEXP.test(cleanUrl)) {
    trailingText = `${cleanUrl.slice(-1)}${trailingText}`
    cleanUrl = cleanUrl.slice(0, -1)
  }

  return {
    cleanUrl,
    trailingText,
  }
}

const renderCardTitleWithLinkButtons = (title: string) => {
  const nodes: ReactNode[] = []
  let lastIndex = 0

  title.replace(TITLE_URL_REGEXP, (match, _url: string, offset: number) => {
    if (offset > lastIndex) {
      nodes.push(title.slice(lastIndex, offset))
    }

    const { cleanUrl, trailingText } = splitTitleUrl(match)
    const href = normalizeTitleUrlHref(cleanUrl)

    nodes.push(
      <span key={`${cleanUrl}-${offset}`} className={classes.titleUrlChunk}>
        <span className={classes.titleUrlText}>{cleanUrl}</span>
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className={classes.titleUrlOpenButton}
          aria-label={`Открыть ссылку ${cleanUrl}`}
          title="Открыть ссылку"
          {...{ [TASK_TRACKER_NO_DND_ATTR]: true }}
          onMouseDown={stopEventPropagation}
          onClick={stopEventPropagation}
        >
          <ExternalLinkIcon className={classes.titleUrlOpenIcon} />
        </a>
        {trailingText}
      </span>,
    )

    lastIndex = offset + match.length
    return match
  })

  if (lastIndex === 0) {
    return title
  }

  if (lastIndex < title.length) {
    nodes.push(title.slice(lastIndex))
  }

  return nodes
}

const TaskTrackerEditableTitle: FC<TaskTrackerEditableTitleProps> = ({
  taskId,
  title,
  variant,
  disabled = false,
  readOnly = false,
  readOnlyReason = null,
  textClassName,
  stopPointerPropagation = false,
  canStartEditByTextClick = false,
  onOpenCard,
  onCopy,
  copyDisabled = false,
  copyDisabledReason = null,
  copyLoading = false,
  onTransfer,
  transferDisabled = false,
  transferDisabledReason = null,
  onDelete,
  deleteDisabled = false,
  deleteDisabledReason = null,
  onUpdated,
  onEditingChange,
}) => {
  const addAlert = useAddAlert()
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  const actionsTriggerRef = useRef<HTMLButtonElement>(null)
  const wasCopyLoadingRef = useRef(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [draftTitle, setDraftTitle] = useState(title)
  const [savingTitle, setSavingTitle] = useState(false)
  const [isActionsOpen, setIsActionsOpen] = useState(false)

  const displayTitle = title?.trim() || '(без названия)'
  const canEditTitle = Boolean(onUpdated) && !disabled && !readOnly
  const canOpenCard = Boolean(onOpenCard)
  const hasRenameAction = Boolean(onUpdated)
  const hasCopyAction = Boolean(onCopy)
  const hasTransferAction = Boolean(onTransfer)
  const hasDeleteAction = Boolean(onDelete)
  const showCardActions =
    variant === 'card' &&
    (canOpenCard ||
      hasRenameAction ||
      hasCopyAction ||
      hasTransferAction ||
      hasDeleteAction)
  const isRenameBlocked = hasRenameAction && (disabled || readOnly)
  const isCopyBlocked = hasCopyAction && (copyDisabled || copyLoading)
  const isTransferBlocked = hasTransferAction && transferDisabled
  const isDeleteBlocked = hasDeleteAction && deleteDisabled
  const titleTextContent = renderCardTitleWithLinkButtons(displayTitle)

  const setEditingState = useCallback(
    (nextValue: boolean) => {
      setIsEditingTitle(nextValue)
      onEditingChange?.(nextValue)
    },
    [onEditingChange],
  )

  useEffect(() => {
    setEditingState(false)
    setDraftTitle(title)
  }, [taskId, setEditingState, title])

  useEffect(() => {
    if (isEditingTitle) return

    setDraftTitle(title)
  }, [isEditingTitle, title])

  const resizeTitleInput = useCallback(() => {
    const input = titleInputRef.current
    if (!input) return

    input.style.height = 'auto'
    input.style.height = `${input.scrollHeight}px`
  }, [])

  useEffect(() => {
    if (!isEditingTitle) return

    resizeTitleInput()
    titleInputRef.current?.focus()
    titleInputRef.current?.select()
  }, [isEditingTitle, resizeTitleInput])

  const startTitleEdit = useCallback(() => {
    if (!canEditTitle || savingTitle) return

    setDraftTitle(title)
    setEditingState(true)
    setIsActionsOpen(false)
  }, [canEditTitle, savingTitle, setEditingState, title])

  const handleStartTitleEdit = useCallback(
    (event: { stopPropagation: () => void; preventDefault: () => void }) => {
      if (stopPointerPropagation) {
        stopEventPropagation(event)
      }
      event.preventDefault()
      startTitleEdit()
    },
    [startTitleEdit, stopPointerPropagation],
  )

  const handleCancelTitleEdit = useCallback(() => {
    setDraftTitle(title)
    setEditingState(false)
  }, [setEditingState, title])

  const handleSaveTitle = useCallback(async () => {
    if (!onUpdated || savingTitle) return

    const trimmedTitle = draftTitle.trim()
    if (!trimmedTitle) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: 'Название задачи не может быть пустым',
      })
      return
    }

    if (trimmedTitle === title.trim()) {
      setEditingState(false)
      return
    }

    setSavingTitle(true)

    try {
      const updatedCard = await taskTrackerApi.updateTask(taskId, {
        title: trimmedTitle,
      })

      onUpdated(updatedCard)
      setEditingState(false)
    } catch (error: unknown) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: getErrorMessage(error, 'Не удалось сохранить название задачи'),
      })
    } finally {
      setSavingTitle(false)
    }
  }, [
    addAlert,
    draftTitle,
    onUpdated,
    savingTitle,
    setEditingState,
    taskId,
    title,
  ])

  const handleTitleBlur = useCallback(() => {
    if (savingTitle) return

    void handleSaveTitle()
  }, [handleSaveTitle, savingTitle])

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setDraftTitle(event.currentTarget.value)
      resizeTitleInput()
    },
    [resizeTitleInput],
  )

  const handleTitleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (stopPointerPropagation) {
        stopEventPropagation(event)
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        handleCancelTitleEdit()
        return
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        event.currentTarget.blur()
      }
    },
    [handleCancelTitleEdit, stopPointerPropagation],
  )

  const handleTextClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!canStartEditByTextClick) return

      handleStartTitleEdit(event)
    },
    [canStartEditByTextClick, handleStartTitleEdit],
  )

  const handleActionButtonMouseDown = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (stopPointerPropagation) {
        stopEventPropagation(event)
      }
    },
    [stopPointerPropagation],
  )

  const closeActionsPopover = useCallback(() => {
    setIsActionsOpen(false)
  }, [])

  useCloseOnAncestorScroll(
    actionsTriggerRef,
    isActionsOpen && !copyLoading,
    closeActionsPopover,
  )

  useEffect(() => {
    if (wasCopyLoadingRef.current && !copyLoading) {
      setIsActionsOpen(false)
    }

    wasCopyLoadingRef.current = copyLoading
  }, [copyLoading])

  const renderActionsMenuItem = (
    label: string,
    onClick: () => void,
    options?: {
      danger?: boolean
      blocked?: boolean
      blockedReason?: string | null
      loading?: boolean
    },
  ) => {
    const isBlocked = Boolean(options?.blocked)
    const isLoading = Boolean(options?.loading)
    const blockedReason = options?.blockedReason ?? null
    const button = (
      <button
        type="button"
        className={cn(classes.actionsMenuItem, {
          [classes.actionsMenuItemDanger]: options?.danger,
          [classes.actionsMenuItemDisabled]: isBlocked,
          [classes.actionsMenuItemLoading]: isLoading,
        })}
        disabled={isBlocked}
        onClick={() => {
          if (isBlocked) return
          onClick()
        }}
      >
        <span className={classes.actionsMenuItemContent}>
          <span>{label}</span>
          {isLoading ? (
            <LoadingIcon spin className={classes.actionsMenuItemSpinner} />
          ) : null}
        </span>
      </button>
    )

    if (!isBlocked || !blockedReason) {
      return button
    }

    return (
      <TaskTrackerPermissionTooltip
        title={blockedReason}
        className={classes.actionsMenuItemWrap}
        block
      >
        {button}
      </TaskTrackerPermissionTooltip>
    )
  }

  const titleNode = (
    <div
      className={cn(classes.text, textClassName, {
        [classes.textCard]: variant === 'card',
        [classes.textDrawer]: variant === 'drawer',
        [classes.textClickable]: canStartEditByTextClick && canEditTitle,
      })}
      {...{
        [TASK_TRACKER_NO_DND_ATTR]: canStartEditByTextClick && canEditTitle,
      }}
      onClick={handleTextClick}
    >
      {titleTextContent}
    </div>
  )

  const titleContent =
    variant === 'card' ? (
      <div className={classes.textCell}>{titleNode}</div>
    ) : (
      titleNode
    )

  return (
    <div
      className={cn(classes.root, {
        [classes.rootEditing]: isEditingTitle,
      })}
      onMouseDown={
        isEditingTitle && stopPointerPropagation
          ? stopEventPropagation
          : undefined
      }
      onClick={
        isEditingTitle && stopPointerPropagation
          ? stopEventPropagation
          : undefined
      }
    >
      {isEditingTitle ? (
        <div
          className={cn(classes.inputRow, {
            [classes.inputRowSaving]: savingTitle,
          })}
          {...{ [TASK_TRACKER_NO_DND_ATTR]: true }}
        >
          <textarea
            ref={titleInputRef}
            className={cn(classes.input, {
              [classes.inputCard]: variant === 'card',
              [classes.inputDrawer]: variant === 'drawer',
            })}
            value={draftTitle}
            maxLength={BOARD_COLUMN_TITLE_MAX_LENGTH}
            rows={1}
            disabled={savingTitle}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
          />
          {savingTitle ? (
            <LoadingIcon spin className={classes.savingIcon} />
          ) : null}
        </div>
      ) : (
        <>
          {titleContent}
          {showCardActions ? (
            <Popover
              trigger="click"
              placement="bottomRight"
              showArrow={false}
              open={isActionsOpen}
              onOpenChange={(nextOpen) => {
                if (copyLoading && !nextOpen) return
                setIsActionsOpen(nextOpen)
              }}
              content={
                <div
                  className={classes.actionsMenu}
                  {...{ [TASK_TRACKER_NO_DND_ATTR]: true }}
                  onMouseDown={handleActionButtonMouseDown}
                >
                  {hasRenameAction
                    ? renderActionsMenuItem('Переименовать', () => startTitleEdit(), {
                        blocked: isRenameBlocked,
                        blockedReason: readOnlyReason,
                      })
                    : null}
                  {canOpenCard
                    ? renderActionsMenuItem('Открыть карточку', () => {
                        onOpenCard?.()
                        setIsActionsOpen(false)
                      })
                    : null}
                  {hasCopyAction
                    ? renderActionsMenuItem(
                        'Копировать задачу',
                        () => {
                          onCopy?.()
                        },
                        {
                          blocked: isCopyBlocked,
                          blockedReason: copyLoading ? null : copyDisabledReason,
                          loading: copyLoading,
                        },
                      )
                    : null}
                  {hasTransferAction
                    ? renderActionsMenuItem(
                        'Перенести задачу',
                        () => {
                          setIsActionsOpen(false)
                          onTransfer?.()
                        },
                        {
                          blocked: isTransferBlocked,
                          blockedReason: transferDisabledReason,
                        },
                      )
                    : null}
                  {hasDeleteAction
                    ? renderActionsMenuItem(
                        'Удалить',
                        () => {
                          setIsActionsOpen(false)
                          onDelete?.()
                        },
                        {
                          danger: true,
                          blocked: isDeleteBlocked,
                          blockedReason: deleteDisabledReason,
                        },
                      )
                    : null}
                </div>
              }
            >
              <button
                ref={actionsTriggerRef}
                type="button"
                className={classes.editButton}
                aria-label="Действия с задачей"
                disabled={savingTitle}
                {...{ [TASK_TRACKER_NO_DND_ATTR]: true }}
                onMouseDown={handleActionButtonMouseDown}
                onClick={(event) => {
                  if (stopPointerPropagation) {
                    stopEventPropagation(event)
                  }
                }}
              >
                <MoreIcon className={classes.editIcon} />
              </button>
            </Popover>
          ) : canEditTitle ? (
            <button
              type="button"
              className={classes.editButton}
              aria-label="Изменить название задачи"
              disabled={savingTitle}
              {...{ [TASK_TRACKER_NO_DND_ATTR]: true }}
              onMouseDown={handleStartTitleEdit}
              onClick={handleStartTitleEdit}
            >
              {savingTitle ? (
                <LoadingIcon spin className={classes.editIcon} />
              ) : (
                <EditIcon className={classes.editIcon} />
              )}
            </button>
          ) : readOnly && Boolean(onUpdated) && readOnlyReason ? (
            <TaskTrackerPermissionTooltip title={readOnlyReason}>
              <button
                type="button"
                className={classes.editButton}
                aria-label="Изменить название задачи"
                disabled
                {...{ [TASK_TRACKER_NO_DND_ATTR]: true }}
                onMouseDown={handleActionButtonMouseDown}
              >
                <EditIcon className={classes.editIcon} />
              </button>
            </TaskTrackerPermissionTooltip>
          ) : null}
        </>
      )}
    </div>
  )
}

export default TaskTrackerEditableTitle
